import { useSyncExternalStore } from 'react';
import { DEVICE_STATUS_EVENT } from '@/api/desktopSync';

export function canCaptureOfflineOrders(roles, status) {
  return Boolean(Array.isArray(roles) && roles.includes('admin')
    && status?.registered === true && status?.isPrimary === true);
}

const observations = new WeakMap();
const emptySnapshot = () => null;
const noSubscription = () => () => {};
const validStatus = value => {
  if (!value || typeof value !== 'object' || Array.isArray(value)
      || typeof value.registered !== 'boolean' || typeof value.isPrimary !== 'boolean'
      || (value.needsRenewal !== undefined && typeof value.needsRenewal !== 'boolean')) return null;
  return Object.freeze({ registered: value.registered, isPrimary: value.isPrimary,
    needsRenewal: value.needsRenewal === true,
    status: typeof value.status === 'string' ? value.status : 'unknown' });
};

// TCA-26101916: installation observation, deliberately independent of login.
// Keep the same bridge's last confirmed authority through transient read failures.
function observationFor(bridge) {
  if (!bridge || typeof bridge !== 'object') return null;
  if (observations.has(bridge)) return observations.get(bridge);
  let snapshot = validStatus(bridge.deviceStatus);
  let sequence = 0;
  let stop;
  const listeners = new Set();
  const current = () => window.lvbDesktop === bridge && listeners.size > 0;
  const publish = value => {
    const next = validStatus(value);
    if (!next) return false;
    if (snapshot && Object.keys(next).every(key => snapshot[key] === next[key])) return true;
    snapshot = next;
    listeners.forEach(notify => notify());
    return true;
  };
  const start = () => {
    const changed = event => {
      if (!current() || !validStatus(event.detail)) return;
      sequence++;
      publish(event.detail);
    };
    window.addEventListener(DEVICE_STATUS_EVENT, changed);
    const ticket = ++sequence;
    const timer = setTimeout(() => { if (sequence === ticket) sequence++; }, 30000);
    Promise.resolve().then(() => {
      if (current() && sequence === ticket && typeof bridge.getDeviceStatus === 'function') return bridge.getDeviceStatus();
      return null;
    }).then(value => {
      if (current() && sequence === ticket) publish(value);
    }).catch(() => { /* Failure is not revocation of confirmed installation authority. */ })
      .finally(() => clearTimeout(timer));
    stop = () => {
      sequence++;
      clearTimeout(timer);
      window.removeEventListener(DEVICE_STATUS_EVENT, changed);
    };
  };
  const observation = {
    getSnapshot: () => window.lvbDesktop === bridge ? snapshot : null,
    subscribe: notify => {
      listeners.add(notify);
      if (listeners.size === 1) start();
      return () => {
        listeners.delete(notify);
        if (!listeners.size) stop();
      };
    },
  };
  observations.set(bridge, observation);
  return observation;
}

export const getDesktopDeviceStatusSnapshot = bridge => observationFor(bridge)?.getSnapshot() || null;

export default function useDesktopDeviceStatus() {
  const bridge = typeof window === 'undefined' ? null : window.lvbDesktop;
  const observation = observationFor(bridge);
  return useSyncExternalStore(observation?.subscribe || noSubscription,
    observation?.getSnapshot || emptySnapshot, emptySnapshot);
}
